import React from 'react';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import { useCompany } from '../context/CompanyProvider';
import { COMPANY_OPTIONS } from '../branding/companies';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Badge,
  Divider,
  FormControl,
  Select,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Forum as MessageBoardIcon,
  EventBusy as TimeOffIcon,
  ReceiptLong as ReceiptIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useHrPendingNotifications } from '../hooks/useHrPendingNotifications';

const headerBadgeSx = {
  '& .MuiBadge-badge': {
    fontSize: '0.65rem',
    height: 16,
    minWidth: 16,
    padding: '0 4px'
  }
};

const Header = ({ onMenuClick, disableMenuButton = false }) => {
  const { tryNavigate } = useDocumentTabs();
  const { user, logout } = useAuth();
  const { company, companyId, switchCompany } = useCompany();
  const {
    manager,
    pendingReceiptCount,
    pendingTimeOffCount,
    unreadMessageCount
  } = useHrPendingNotifications();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const showHrNotifications = Boolean(manager);

  const handleCompanyChange = (e) => {
    switchCompany(e.target.value);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const goTo = (path) => {
    void tryNavigate(path);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
      }}
    >
      <Toolbar variant="dense" sx={{ minWidth: 0, maxWidth: '100%' }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={disableMenuButton ? undefined : onMenuClick}
          edge="start"
          size="small"
          disabled={disableMenuButton}
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          onClick={() => void tryNavigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexGrow: 1,
            minWidth: 0,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src={company.logos.header}
            alt={company.shortName}
            sx={{ height: 26, width: 'auto', borderRadius: 0.5, display: { xs: 'none', sm: 'block' } }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontSize: '1.05rem', fontWeight: 700, letterSpacing: 0.2 }}
          >
            {company.shortName}
          </Typography>
        </Box>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {showHrNotifications ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 0.5 }}>
                <Tooltip title="Employee messages">
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="Employee messages"
                    onClick={() => goTo('/hr/employee-messages')}
                  >
                    <Badge
                      badgeContent={unreadMessageCount}
                      color="warning"
                      max={99}
                      invisible={unreadMessageCount <= 0}
                      sx={headerBadgeSx}
                    >
                      <MessageBoardIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Time off requests">
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="Time off requests"
                    onClick={() => goTo('/hr/time-off-requests')}
                  >
                    <Badge
                      badgeContent={pendingTimeOffCount}
                      color="warning"
                      max={99}
                      invisible={pendingTimeOffCount <= 0}
                      sx={headerBadgeSx}
                    >
                      <TimeOffIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Expense reimbursements">
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="Expense reimbursements"
                    onClick={() => goTo('/hr/expense-reimbursements')}
                  >
                    <Badge
                      badgeContent={pendingReceiptCount}
                      color="warning"
                      max={99}
                      invisible={pendingReceiptCount <= 0}
                      sx={headerBadgeSx}
                    >
                      <ReceiptIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={companyId}
                onChange={handleCompanyChange}
                displayEmpty
                renderValue={(v) => COMPANY_OPTIONS.find((c) => c.id === v)?.label ?? 'Company'}
                sx={{ fontSize: '0.875rem', height: 36, backgroundColor: 'grey.50' }}
              >
                {COMPANY_OPTIONS.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <ListItemText primary={c.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mr: 1, fontSize: '0.875rem' }}>
              Welcome, {user.first_name}
            </Typography>

            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
              </Avatar>
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  void tryNavigate('/profile');
                }}
              >
                <AccountCircle sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <Settings sx={{ mr: 1 }} />
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
